import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"

export default class extends Controller {
  static values = {
    savePath: String,
    debounceMs: { type: Number, default: 500 }
  }

  connect() {
    // Create debounced save function with the correct debounce time
    this.debouncedSave = debounce(() => {
      this.save()
    }, this.debouncesMsValue || 500)
  }

  disconnect() {
    if (this.debouncedSave) {
      this.debouncedSave.cancel()
    }
  }

  autoSave() {
    this.debouncedSave()
  }

  async save() {
    const form = this.element.querySelector('form')
    if (!form || !form.elements) {
      console.error("Form not found or invalid.")
      return
    }

    // Validate rate limit fields
    if (!this.validateRateLimits(form)) {
      return
    }

    // Initialize FormData
    const formData = new FormData(form)

    try {
      const response = await fetch(this.savePathValue || form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const html = await response.text()
      if (html.trim()) {
        Turbo.renderStreamMessage(html)
      }

      // Update the iframe if it exists
      this.updatePreviewFrame()

      // Display feedback to the user
      this.showSaveStatus(true)

    } catch (error) {
      console.error('Save failed:', error)
      this.showSaveStatus(false)
    }
  }
  
  // Handle manual reload of the iframe via reload button
  reloadIframe() {
    // Find the iframe
    const iframe = document.querySelector('[data-dashboard--integration-target="previewFrame"]')
    if (iframe && iframe.src) {
      // Show a small loading animation on the button
      const button = event.currentTarget
      const icon = button.querySelector('i')
      
      if (icon) {
        // Add spinning animation
        const originalClass = icon.className
        icon.className = 'fas fa-spinner fa-spin'
        
        // Reset the icon after reload completes
        setTimeout(() => {
          icon.className = originalClass
        }, 1000)
      }
      
      // Force reload by adding timestamp parameter
      const url = new URL(iframe.src)
      url.searchParams.set('t', Date.now())
      iframe.src = url.toString()
    }
  }
  
  // Update the preview iframe when form settings change
  updatePreviewFrame() {
    // Find the iframe via the data-dashboard--integration-target="previewFrame" attribute
    const iframe = document.querySelector('[data-dashboard--integration-target="previewFrame"]')
    if (iframe && iframe.src) {
      console.log("Updating iframe preview after settings save")
      
      // Add a timestamp to force a refresh
      const url = new URL(iframe.src)
      url.searchParams.set('t', Date.now())
      iframe.src = url.toString()
      
      // Setup iframe resize handling
      iframe.onload = () => {
        try {
          // Wait a moment for the content to fully render
          setTimeout(() => {
            const height = iframe.contentWindow.document.body.scrollHeight
            iframe.style.height = (height + 30) + 'px'
            
            // Also setup a ResizeObserver if possible
            try {
              // Check if we already have a resize observer attached
              if (iframe._resizeObserver) {
                iframe._resizeObserver.disconnect()
              }
              
              // Create a new resize observer
              const resizeObserver = new ResizeObserver(() => {
                const newHeight = iframe.contentWindow.document.body.scrollHeight
                iframe.style.height = (newHeight + 30) + 'px'
              })
              
              // Start observing the body element
              resizeObserver.observe(iframe.contentWindow.document.body)
              
              // Store the observer for cleanup later
              iframe._resizeObserver = resizeObserver
            } catch (e) {
              console.warn("Could not set up ResizeObserver:", e)
            }
          }, 300)
        } catch (e) {
          console.warn("Error adjusting iframe height:", e)
        }
      }
    }
  }

  showSaveStatus(success, message = null) {
    const statusElement = document.createElement('div')
    statusElement.className = `save-status ${success ? 'save-success' : 'save-error'}`
    
    if (success) {
      statusElement.innerHTML = '<i class="fas fa-check"></i> Saved'
    } else {
      statusElement.innerHTML = `<i class="fas fa-exclamation-circle"></i> ${message || 'Error saving'}`
    }
    
    document.body.appendChild(statusElement)
    
    // Add animation class
    setTimeout(() => statusElement.classList.add('show'), 10)
    
    // Remove after delay
    setTimeout(() => {
      statusElement.classList.remove('show')
      setTimeout(() => statusElement.remove(), 300)
    }, 3000) // Slightly longer timeout for error messages
  }

  getCSRFToken() {
    const element = document.querySelector('meta[name="csrf-token"]')
    return element ? element.getAttribute('content') : null
  }
  
  validateRateLimits(form) {
    // Get rate limit fields
    const hourlyLimit = form.elements['hosted_settings[hosted_solution_rate_limit_1h]']
    const dailyLimit = form.elements['hosted_settings[hosted_solution_rate_limit_24h]']
    
    if (!hourlyLimit || !dailyLimit) {
      return true // If fields don't exist, skip validation
    }
    
    const hourlyValue = parseInt(hourlyLimit.value)
    const dailyValue = parseInt(dailyLimit.value)
    
    // Get translations (assuming they're loaded in the global I18n object)
    const translations = {
      hourlyLimit: I18n?.t('dashboard.integration.hosted_verification.validation.hourly_limit') || "Hourly rate limit must be a number between 1-999",
      dailyLimit: I18n?.t('dashboard.integration.hosted_verification.validation.daily_limit') || "Daily rate limit must be a number between 1-9999",
      dailyGreaterThanHourly: I18n?.t('dashboard.integration.hosted_verification.validation.daily_greater_than_hourly') || "Daily rate limit must be greater than hourly rate limit"
    }
    
    // Validate hourly limit
    if (isNaN(hourlyValue) || hourlyValue < 1 || hourlyValue > 999) {
      this.showValidationError(hourlyLimit, translations.hourlyLimit)
      return false
    }
    
    // Validate daily limit
    if (isNaN(dailyValue) || dailyValue < 1 || dailyValue > 9999) {
      this.showValidationError(dailyLimit, translations.dailyLimit)
      return false
    }
    
    // Validate daily > hourly
    if (dailyValue <= hourlyValue) {
      this.showValidationError(dailyLimit, translations.dailyGreaterThanHourly)
      return false
    }
    
    // Clear any previous validation errors
    hourlyLimit.classList.remove('is-invalid')
    dailyLimit.classList.remove('is-invalid')
    
    // Remove any error feedback elements
    const feedbackElements = this.element.querySelectorAll('.invalid-feedback')
    feedbackElements.forEach(element => element.remove())
    
    return true
  }
  
  showValidationError(inputElement, message) {
    // Add error class to input
    inputElement.classList.add('is-invalid')
    
    // Remove any existing error message
    const existingFeedback = inputElement.parentNode.querySelector('.invalid-feedback')
    if (existingFeedback) {
      existingFeedback.remove()
    }
    
    // Create error message element
    const feedbackElement = document.createElement('div')
    feedbackElement.className = 'invalid-feedback'
    feedbackElement.innerText = message
    
    // Add error message after input
    inputElement.parentNode.appendChild(feedbackElement)
    
    // Show error alert
    this.showSaveStatus(false, message)
  }
  
  // Copy URL from the URL bar to clipboard
  copyUrl(event) {
    // Find the URL element
    const urlElement = event.target.closest('.url-bar').querySelector('.text-truncate')
    
    if (!urlElement) return
    
    // Copy the URL to clipboard
    navigator.clipboard.writeText(urlElement.textContent.trim())
      .then(() => {
        // If there's an icon in the button, change it temporarily to show confirmation
        const button = event.target.closest('button')
        const icon = button.querySelector('i')
        
        if (icon) {
          const originalClass = icon.className
          icon.className = 'fas fa-check'
          
          // Reset icon after a short delay
          setTimeout(() => {
            icon.className = originalClass
          }, 1500)
        }
        
        // Show a brief "Copied" tooltip
        const successMessage = I18n?.t('dashboard.integration.js.url_copied') || 'URL copied!'
        this.showSaveStatus(true, successMessage)
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err)
        const errorMessage = I18n?.t('dashboard.integration.js.failed_to_copy') || 'Failed to copy URL'
        this.showSaveStatus(false, errorMessage)
      })
  }
  
  // Copy to clipboard functionality
  copyToClipboard(event) {
    // Find the input field to copy
    const inputElement = event.target.closest('.input-group').querySelector('input')
    
    if (!inputElement) return
    
    // Copy the value to clipboard
    navigator.clipboard.writeText(inputElement.value)
      .then(() => {
        // Show success status
        const successMessage = I18n?.t('dashboard.integration.installation.copied') || 'Copied to clipboard!'
        this.showSaveStatus(true, successMessage)
        
        // If there's an icon in the button, change it temporarily to show confirmation
        const button = event.target.closest('button')
        const icon = button.querySelector('i')
        
        if (icon) {
          const originalClass = icon.className
          icon.className = 'fas fa-check'
          
          // Reset icon after a short delay
          setTimeout(() => {
            icon.className = originalClass
          }, 1500)
        }
      })
      .catch(err => {
        console.error('Failed to copy text: ', err)
        const errorMessage = I18n?.t('dashboard.integration.js.failed_to_copy') || 'Failed to copy to clipboard'
        this.showSaveStatus(false, errorMessage)
      })
  }
}